import type { ApiErrorCode } from '@lombok/shared';

/**
 * Error thrown by the typed client whenever the API returns a failure envelope
 * ({ success: false, error }) or the transport itself fails (network/timeout/
 * non-JSON body). UI code can branch on `code` to render Spanish-first messages
 * and on `status` for transport-level handling (e.g. force re-login on 401).
 *
 * The message is taken verbatim from the API when available — backend messages
 * are already Spanish-first and user-safe — and otherwise falls back to a
 * generic Spanish message so we never surface a raw stack to the user.
 */
export class ApiError extends Error {
  /** Stable machine-readable error code (e.g. INSUFFICIENT_STOCK, UNAUTHORIZED). */
  readonly code: ApiErrorCode | string;
  /** HTTP status code, when a response was received. */
  readonly status?: number;
  /** Correlation id echoed by the API; surface it in support flows / logs. */
  readonly requestId?: string;
  /** Optional structured detail (e.g. Zod field issues for VALIDATION_ERROR). */
  readonly details?: unknown;

  constructor(params: {
    code: ApiErrorCode | string;
    message: string;
    status?: number;
    requestId?: string;
    details?: unknown;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.code = params.code;
    this.status = params.status;
    this.requestId = params.requestId;
    this.details = params.details;
    // Preserve prototype chain when targeting ES5-ish runtimes.
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /** True when the failure is an authentication problem (caller should re-login). */
  get isUnauthorized(): boolean {
    return this.status === 401 || this.code === 'UNAUTHORIZED';
  }

  /** True when the caller is authenticated but lacks permission. */
  get isForbidden(): boolean {
    return this.status === 403 || this.code === 'FORBIDDEN';
  }
}

/** Generic Spanish fallback used when the API gives us nothing usable. */
export const GENERIC_ERROR_MESSAGE_ES =
  'Ocurrió un error inesperado. Intenta de nuevo en unos momentos.';
